import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Sidebar as UISidebar,
  useSidebar,
} from "@ui/components/ui/sidebar";
import {
  Calendar,
  GoogleDocs,
  Group,
  Gym,
  Keyframes,
  Label,
  MultiplePages,
  PeopleTag,
  ViewGrid,
} from "iconoir-react";
import { observer } from "mobx-react-lite";
import { type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { LogoBeta } from "ui";

import { useAuthContext, useAuthStore } from "src/auth";

import {
  LanguageSwitchButton,
  useLanguageSwitchContext,
} from "../I18N/LanguageSwitchContext";

import { type NavButtonProps, NavButtonV2 } from "./NavButton";

export const Sidebar = observer(function Sidebar() {
  const authContext = useAuthContext();

  if (authContext.store.authenticatedData.coach) {
    return <CoachSidebar />;
  } else if (authContext.store.authenticatedData.athlete) {
    return <AthleteSidebar />;
  }

  return null;
});

type BaseSidebarProps = PropsWithChildren;

function BaseSidebar(props: BaseSidebarProps) {
  const sidebar = useSidebar();

  const languageSwitchContext = useLanguageSwitchContext();

  return (
    <UISidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {sidebar.state === "expanded" ? (
              <SidebarMenuButton asChild>
                <Link to={"/"}>
                  <LogoBeta className="!w-full !h-full" />
                </Link>
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton asChild className="p-0">
                <Link to={"/"}>
                  <img className="size-4" src="/assets/logo-small.svg" />
                </Link>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>{props.children}</SidebarContent>
      <SidebarFooter>
        {sidebar.state === "expanded" ? (
          <SidebarMenu>
            <div className="flex gap-2">
              {languageSwitchContext.availableLocaleOptions.map((option) => {
                return (
                  <SidebarMenuItem key={option.locale}>
                    <LanguageSwitchButton
                      key={option.locale}
                      locale={option.locale}
                      label={option.label}
                    />
                  </SidebarMenuItem>
                );
              })}
            </div>
          </SidebarMenu>
        ) : null}
      </SidebarFooter>
    </UISidebar>
  );
}

const CoachSidebar = observer(function () {
  const { t } = useTranslation();

  const mainNav: NavButtonProps[] = [
    {
      icon: <Calendar />,
      text: t("schedule"),
      href: "/",
      activeWhen: (match) => {
        return match?.pathname === "/";
      },
    },
    {
      icon: <PeopleTag />,
      text: t("sidebar.athletes"),
      href: "/athletes",
      activeWhen: (match) => {
        return match?.pathname === "/athletes";
      },
    },
    {
      icon: <GoogleDocs />,
      text: t("sidebar.forms"),
      href: "/questionnaires",
      activeWhen: (match) => {
        return match?.pathname === "/questionnaires";
      },
    },
  ];

  const libraryNav: NavButtonProps[] = [
    {
      icon: <MultiplePages />,
      text: t("sidebar.programs"),
      href: "/library/programs",
      activeWhen: (match) => {
        return match?.pathname === "/library/programs";
      },
    },
    {
      icon: <Keyframes />,
      text: t("sidebar.workouts"),
      href: "/library/workouts",
      activeWhen: (match) => {
        return match?.pathname === "/library/workouts";
      },
    },
    {
      icon: <Gym />,
      text: t("sidebar.exercises"),
      href: "/library/exercises",
      activeWhen: (match) => {
        return match?.pathname === "/library/exercises";
      },
    },
  ];

  const navSecondary: NavButtonProps[] = [
    {
      icon: <Label />,
      text: t("sidebar.labels"),
      href: "/labels",
      activeWhen: (match) => {
        return match?.pathname === "/labels";
      },
    },
  ];

  return (
    <BaseSidebar>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {mainNav.map((props) => {
              return (
                <SidebarMenuItem key={props.href}>
                  <NavButtonV2 {...props} />
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <SidebarGroup>
        <SidebarGroupLabel>{t("sidebar.yourLibrary")}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {libraryNav.map((props) => {
              return (
                <SidebarMenuItem key={props.href}>
                  <NavButtonV2 {...props} />
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {navSecondary.map((props) => {
              return (
                <SidebarMenuItem key={props.href}>
                  <NavButtonV2 {...props} />
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <AdminThingy />
    </BaseSidebar>
  );
});

function AthleteSidebar() {
  const { t } = useTranslation();

  const mainNav: NavButtonProps[] = [
    {
      icon: <ViewGrid />,
      text: "Mój profil",
      href: "/",
      activeWhen: (match) => {
        return match?.pathname === "/";
      },
    },
    {
      icon: <Group />,
      text: t("invitations"),
      href: "/invitations",
      activeWhen: (match) => {
        return match?.pathname === "/invitations";
      },
    },
    {
      icon: <Calendar />,
      text: t("sidebar.calendar"),
      href: "/calendar",
      activeWhen: (match) => {
        return match?.pathname === "/calendar";
      },
    },
    {
      icon: <GoogleDocs />,
      text: t("sidebar.forms"),
      href: "/questionnaire-submissions",
      activeWhen: (match) => {
        return match?.pathname === "/questionnaire-submissions";
      },
    },
  ];

  return (
    <BaseSidebar>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {mainNav.map((props) => (
              <SidebarMenuItem key={props.href}>
                <NavButtonV2 {...props} />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </BaseSidebar>
  );
}

const AdminThingy = observer(function AdmingThingy() {
  const authStore = useAuthStore();

  if (!authStore.isAdmin) {
    return null;
  }

  const adminNavigation: NavButtonProps[] = [
    {
      icon: <Label />,
      text: "Promo codes",
      href: "/admin/invitation-codes",
      activeWhen: (match) => {
        return match?.pathname === "/admin/invitation-codes";
      },
    },
  ];

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Admin thingy</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {adminNavigation.map((props) => {
            return (
              <SidebarMenuItem key={props.href}>
                <NavButtonV2 {...props} />
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
});
